import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import * as Icons from "lucide-react";
import { Flame, Trophy, Sparkles, ArrowRight, Star } from "lucide-react";

const COIN_3D = "/lightning-3d.png";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-white/60">Loading...</div>;
  const { next_tasks, category_progress, badges, xp_to_next_level, user: u } = data;
  const xpThis = u.xp;
  const xpNeeded = xp_to_next_level;
  const pct = Math.min(100, Math.round((xpThis / xpNeeded) * 100));

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="glass-card p-6 lg:col-span-2 relative overflow-hidden">
          <img src={COIN_3D} alt="" className="absolute -right-6 -top-4 w-40 opacity-90 animate-float pointer-events-none" style={{ mixBlendMode: "screen" }}/>
          <div className="text-xs font-bold tracking-widest text-neoncyan">G'DAY</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight mt-1" data-testid="dashboard-greeting">
            {u.name?.split(" ")[0]}, you're <span className="text-gradient-pink">level {u.level}</span>.
          </h1>
          <p className="text-white/65 mt-2 max-w-md">Keep ticking off tasks to unlock badges, perks and your next level.</p>

          <div className="mt-6 max-w-md">
            <div className="flex justify-between text-xs text-white/60 mb-1.5">
              <span>{xpThis} XP</span><span>{xpNeeded} XP to lvl {u.level + 1}</span>
            </div>
            <div className="h-3 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-neoncyan to-neongreen shadow-glowcyan" style={{ width: `${pct}%` }} data-testid="xp-progress-bar"/>
            </div>
          </div>
        </div>

        <div className="glass-card p-6 flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-neonpink/15 border border-neonpink/30 flex items-center justify-center">
              <Flame className="text-neonpink"/>
            </div>
            <div>
              <div className="text-3xl font-display font-black" data-testid="streak-count">{u.streak}</div>
              <div className="text-xs text-white/60 uppercase tracking-widest">Day streak</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-5 text-sm">
            <div><div className="text-2xl font-display font-bold text-neoncyan" data-testid="completed-count">{u.completed_tasks?.length || 0}</div><div className="text-xs text-white/55">Tasks done</div></div>
            <div><div className="text-2xl font-display font-bold text-neonyellow" data-testid="badges-count">{u.badges?.length || 0}</div><div className="text-xs text-white/55">Badges</div></div>
          </div>
        </div>
      </div>

      {/* Category progress */}
      <section>
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-display text-2xl font-extrabold tracking-tight">Your roadmap</h2>
          <Link to="/categories" className="text-xs text-neoncyan font-semibold flex items-center gap-1" data-testid="see-all-categories">See all <ArrowRight size={12}/></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {category_progress.map(c => {
            const Icon = Icons[c.icon] || Icons.Sparkles;
            const pct = c.total ? Math.round((c.done / c.total) * 100) : 0;
            return (
              <Link key={c.category_id} to={`/categories/${c.category_id}`} className="glass-card p-5 hover:border-white/25 transition" data-testid={`category-progress-${c.category_id}`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${c.color}22`, color: c.color, boxShadow: `0 0 20px ${c.color}33` }}>
                  <Icon size={20}/>
                </div>
                <div className="font-display font-bold text-sm">{c.name}</div>
                <div className="text-xs text-white/55 mt-0.5">{c.done}/{c.total} done</div>
                <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mt-3">
                  <div className="h-full" style={{ width: `${pct}%`, background: c.color }}/>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Next tasks */}
      <section>
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-display text-2xl font-extrabold tracking-tight">Next up</h2>
          <span className="text-xs text-white/50">Recommended for you</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {next_tasks.map(t => (
            <Link key={t.task_id} to={`/tasks/${t.task_id}`} className="glass-card p-5 group hover:border-neonpink/40 transition" data-testid={`next-task-${t.task_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="font-display font-bold text-base group-hover:text-gradient-pink">{t.title}</div>
                <div className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-neonyellow/15 text-neonyellow font-bold whitespace-nowrap">
                  <Star size={12}/> +{t.points}
                </div>
              </div>
              <div className="text-xs text-white/60 mt-1.5 line-clamp-2">{t.why}</div>
              <div className="flex gap-3 text-xs text-white/50 mt-3">
                <span>⏱ {t.estimated_time}</span>
                <span className="capitalize">· {t.difficulty}</span>
              </div>
            </Link>
          ))}
          {next_tasks.length === 0 && (
            <div className="glass-card p-8 text-center text-white/60">
              <Trophy className="mx-auto text-neonyellow mb-3" size={32}/>
              You've completed every task! Adulting champion.
            </div>
          )}
        </div>
      </section>

      {/* Badges */}
      {badges.length > 0 && (
        <section>
          <div className="flex items-end justify-between mb-4">
            <h2 className="font-display text-2xl font-extrabold tracking-tight">Your badges</h2>
            <Sparkles className="text-neonyellow" size={18}/>
          </div>
          <div className="flex flex-wrap gap-3">
            {badges.map(b => {
              const Icon = Icons[b.icon] || Icons.Award;
              return (
                <div key={b.badge_id} className="glass-card px-4 py-3 flex items-center gap-3" data-testid={`badge-${b.badge_id}`}>
                  <div className="w-10 h-10 rounded-xl bg-neonyellow/15 text-neonyellow flex items-center justify-center"><Icon/></div>
                  <div>
                    <div className="font-bold text-sm">{b.name}</div>
                    <div className="text-xs text-white/55">{b.description}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
